<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import OnboardingAPI from 'dashboard/api/onboarding';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { resolveTargetInbox } from './resolveTargetInbox';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const isAiAvailable = computed(
  () => props.progress.capabilities?.ai_agent_available
);
const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(4)
);

const inboxId = ref(null);
const selectedMode = ref(null);
const saveError = ref(null);

const isValid = computed(() => !!selectedMode.value);

watch(isValid, value => emit('update:can-advance', value), { immediate: true });

// 'open_queue' is the inbox's real default with no explicit choice ever made, so it can't
// be told apart from "never visited this step" — only trust it as a real answer once the
// step is already marked completed (an explicit past choice) or a different mode is set.
const resolveMode = inbox =>
  inbox.distribution_mode !== 'open_queue' || isStepAlreadyCompleted.value
    ? inbox.distribution_mode
    : null;

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 4])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const persistMode = async () => {
  if (!inboxId.value) return;
  try {
    await InboxesAPI.setDistributionMode(inboxId.value, selectedMode.value);
    saveError.value = null;
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    saveError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_4.UPDATE_ERROR');
  }
};

const selectMode = value => {
  selectedMode.value = value;
  persistMode();
};

// Re-syncs when the inbox changes from another screen so both views stay live.
const refreshFromServer = async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (!inbox) return;

    inboxId.value = inbox.id;
    selectedMode.value = resolveMode(inbox);
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (inbox) {
      inboxId.value = inbox.id;
      selectedMode.value = resolveMode(inbox);
    }
    if (isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_4.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/416-distribuicao-dos-atendimentos-quem-recebe-o-proximo-lead"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_4.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_4.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_4.DESCRIPTION') }}</p>

      <div role="radiogroup" class="flex flex-col w-full max-w-2xl gap-3">
        <label
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedMode === 'round_robin' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <input
            type="radio"
            name="distribution-mode"
            value="round_robin"
            :checked="selectedMode === 'round_robin'"
            class="mt-1 h-4 w-4 shrink-0 accent-n-brand"
            @change="selectMode('round_robin')"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_4.ROUND_ROBIN_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_4.ROUND_ROBIN_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <label
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedMode === 'open_queue' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <input
            type="radio"
            name="distribution-mode"
            value="open_queue"
            :checked="selectedMode === 'open_queue'"
            class="mt-1 h-4 w-4 shrink-0 accent-n-brand"
            @change="selectMode('open_queue')"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_4.OPEN_QUEUE_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_4.OPEN_QUEUE_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <label
          v-if="isAiAvailable"
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedMode === 'ai_first' ? 'border-n-brand bg-n-brand/5' : ''
          "
        >
          <input
            type="radio"
            name="distribution-mode"
            value="ai_first"
            :checked="selectedMode === 'ai_first'"
            class="mt-1 h-4 w-4 shrink-0 accent-n-brand"
            @change="selectMode('ai_first')"
          />
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_4.AI_FIRST_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_4.AI_FIRST_DESCRIPTION') }}
            </span>
          </span>
        </label>

        <p v-if="saveError" class="text-sm text-n-ruby-9">{{ saveError }}</p>
      </div>
    </div>
  </div>
</template>
